<template>
  <div class="purchase-body">
    <h2 class="marketplace-title">{{ $t('mainMarketplaceTitle') }}</h2>
    <div class="grid-wrapper">
      <SaleStatus :key="version" />

      <el-dialog
        v-model="showBundleModal"
        :title="$t('buypackButtonDialogTItle')"
        class="gradient-blue-dark multi-token-modal"
        :class="{ 'ru-locale': $i18n.locale === 'ru', 'multi-token-modal-width': $device.isMobile }"
        :append-to-body="true"
        :fullscreen="$device.isMobile"
        destroy-on-close
        @close="resetActiveBundle"
      >
        <MultiTokenPackModal
          v-if="activeBundle"
          :data="activeBundle"
          :is-enough-balance="isEnoughBalance"
          :button-loading="isButtonLoading"
          @click="handleClickBuy"
        />
      </el-dialog>

      <el-dialog
        v-model="showInsufficientFundsDialog"
        :title="$t('buypackButtonInsufficientFunds')"
        :fullscreen="$device.isMobileOrTablet"
        class="insufficient-funds-modal"
        :class="{ 'ru-locale': $i18n.locale === 'ru' }"
        append-to-body
        destroy-on-close
        align-center
      >
        <InsufficientFundsModal :price="transactionPrice" @close="showInsufficientFundsDialog = false">
          <template #main-text>
            <span v-html="$t('bundlesInsufficientText')" />
          </template>
        </InsufficientFundsModal>
      </el-dialog>

      <el-dialog
        v-model="showSuccessDialog"
        :fullscreen="$device.isMobile"
        :title="$t('corePurchaseSuccessful')"
        append-to-body
        destroy-on-close
        class="gradient-blue-dark multi-token-modal-success"
        :class="{ 'ru-locale': $i18n.locale === 'ru', 'multi-token-modal-width': $device.isMobile }"
        @closed="resetPurchasedBundle"
      >
        <MultiTokenPackModalSuccess
          v-if="purchasedBundle"
          :data="purchasedBundle"
          :link-to-transaction="linkToTransaction"
          @click="showSuccessDialog = false"
        />
      </el-dialog>

      <div class="shop-wrapper">
        <div class="offers-wrapper card main-cards">
          <h4 class="offers-title">{{ $t('mainTortugaMarketplaceTitle') }}</h4>
          <MyTokens :options="options" reset-on-destroy :selected="activeTab" @set-active-tab="setActiveTab" />
          <div v-if="activeTab === MarketplaceSections.TokenSets">
            <el-skeleton :loading="!bundles?.length" animated class="offers-bundles-skeleton">
              <template #template>
                <el-skeleton-item
                  v-for="(item, index) in new Array(8)"
                  :key="index"
                  class="offers-bundles-skeleton-item"
                />
              </template>

              <template #default>
                <div class="offers-bundles-list">
                  <MultiTokenPackCard
                    v-for="bundle in bundles"
                    :key="bundle.bundleId"
                    :data="bundle"
                    @click="() => handleClickOpen(bundle.bundleId)"
                  />
                </div>
              </template>
            </el-skeleton>
          </div>
          <div v-else :key="activeTab" class="offers-list">
            <BuyPackButton
              v-for="pack in packs"
              :key="pack.key"
              :pack-key="pack.key"
              :price="pack.price"
              :token-reward="pack.tokenReward"
              :extra-rewards="pack.extraRewards"
              :extra-reward-key="pack.extraRewardKey || ''"
              :name="pack.name"
              :image-path="pack.imagePath"
              :token="pack.token"
              :pack-description-key="pack?.descriptionKey"
              @success="updateData"
            />
          </div>
          <template v-if="activeTab === Tokens.pearl">
            <p class="text-block">
              {{ $t('marketplacePearlTextBlock1') }}
            </p>
            <p class="text-block">
              {{ $t('marketplacePearlTextBlock2') }}
            </p>
          </template>
          <template v-else-if="activeTab !== MarketplaceSections.TokenSets">
            <p class="text-block">
              {{
                $t('marketplaceOtherTokenTextBlock1', {
                  token: tokenConfig.methodName.toUpperCase(),
                  tokenDeclensed: tokenConfig.methodName.toUpperCase()
                })
              }}
            </p>
            <p class="text-block">
              {{
                $t('marketplaceOtherTokenTextBlock2', {
                  token: tokenConfig.methodName.toUpperCase(),
                  tokenDeclensed: tokenConfig.methodName.toUpperCase(),
                  tokenSingle: tokenConfig.methodName.toUpperCase()
                })
              }}
            </p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useHeadSafe } from '@unhead/vue';
import {
  CLAY_PACK_DATA,
  COAL_PACK_DATA,
  GOLD_ORE_PACK_DATA,
  IRON_ORE_PACK_DATA,
  PEARL_PACK_DATA,
  SAND_PACK_DATA,
  WOOD_PACK_DATA
} from '~/utils/constants/marketplace';
import { apiUrls, Tokens, tokensConfig } from '~/utils/constants';
import { type CustomButton, type ITokenOptionItem, MarketplaceSections } from '~/types/marketplace';
import type { ITokenModel } from '~/types/apiService';
import useEnvs from '~/composables/useEnvs';
import useTokensApr from '~/composables/useTokensApr';
import { ElementType } from '~/utils/constants/ui';
import tokens from 'public/img/tokens';
import { Formatters } from '~/utils/formatters/formatters';

definePageMeta({
  middleware: [
    function () {
      const store = useMainStore();
      store.setCurrentToken(Tokens.pearl);
    }
  ]
});

const {
  bundles,
  showBundleModal,
  isButtonLoading,
  activeBundle,
  purchasedBundle,
  showInsufficientFundsDialog,
  showSuccessDialog,
  transactionPrice,
  linkToTransaction,
  resetActiveBundle,
  resetPurchasedBundle,
  handleClickOpen,
  handleClickBuy,
  isEnoughBalance
} = await useBuyBundle();

const { apiUrl } = useEnvs();
const { t } = useI18n();

const { data: tokensData } = useFetch<ITokenModel[]>(apiUrls.token.tokens, {
  query: {},
  baseURL: apiUrl,
  immediate: true,
  server: false
});

const { allAprStatus, findApr } = useTokensApr(tokensData, 'marketplace');

const activeTab = ref<MarketplaceSections | Tokens>(Tokens.pearl);

const setActiveTab = (tab: MarketplaceSections | Tokens) => {
  activeTab.value = tab;
};

const packs = computed(() => {
  return tokenSwitchHandler(activeTab.value).sort((a, b) => a.price - b.price);
});

const tokenConfig = computed(() => {
  return tokensConfig[activeTab.value as Tokens];
});

const tokenSwitchHandler = (token: string) => {
  switch (token) {
    case Tokens.pearl:
      return PEARL_PACK_DATA;
    case Tokens.coal:
      return COAL_PACK_DATA;
    case Tokens.goldOre:
      return GOLD_ORE_PACK_DATA;
    case Tokens.ironOre:
      return IRON_ORE_PACK_DATA;
    case Tokens.wood:
      return WOOD_PACK_DATA;
    case Tokens.sand:
      return SAND_PACK_DATA;
    case Tokens.clay:
      return CLAY_PACK_DATA;
    case Tokens.gold:
      return [];
    default:
      return PEARL_PACK_DATA;
  }
};

useHeadSafe({
  title: t('mainIndexTitle')
});
const version = ref(0);

function updateData() {
  version.value = version.value++;
}

const multiTokenChapter: CustomButton = {
  type: ElementType.Button,
  key: MarketplaceSections.TokenSets,
  label: 'buyPackSetChevron',
  sectionLabel: 'marketplaceSectionTokenSets',
  src: '/img/icons/tokenSets.png'
};

const options = computed(() => {
  if (!tokensData.value?.length || allAprStatus.value !== 'success') return [];
  const filtered = tokensData.value
    ?.filter((token) => token.isSellable)
    .map((item) => {
      return {
        ...item,
        apr: findApr(item.key),
        label: Formatters.text.splitCamelCase(tokensConfig[item.key].methodName),
        src: tokens[item.key]
      } as ITokenOptionItem;
    });

  const tokensWithChapter = [...filtered, multiTokenChapter];

  return tokensWithChapter;
});

provide('allAprStatus', allAprStatus);
provide('findApr', findApr);
</script>

<style scoped lang="scss">
.text-container {
  padding: 0 22px;
}

.text-block {
  @media screen and (max-width: 990px) {
    text-align: center;
  }
}

.text-block:last-child {
  margin-bottom: 12px;
}

.purchase-body {
  display: flex;
  justify-content: center;
  gap: 37px;
  flex-wrap: wrap;
  align-items: flex-start;
  position: relative;
  margin-top: 40px;

  @media screen and (max-width: 479px) {
    margin-top: 0;
  }

  @media screen and (max-width: 900px) {
    gap: 0;
  }

  @media screen and (max-width: 767px) {
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
}

.marketplace-title {
  color: #fff;
  font-size: 64px;
  font-weight: 900;
  line-height: 94.72px;
  margin: 0;

  @media screen and (max-width: 990px) {
    margin-top: 35px;
    margin-bottom: 35px;
    font-size: 3em;
  }

  @media screen and (max-width: 767px) {
    margin-top: 0;
    margin-bottom: 0;
  }
}

.offers-list {
  margin-bottom: 40px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;

  .card {
    width: 270px;
  }
}

.offers-bundles-list,
.offers-bundles-skeleton {
  margin-bottom: 40px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  justify-content: center;
  max-width: 100%;

  .offers-bundles-skeleton-item {
    width: 270px;
    height: 472px;
    min-height: 472px;
  }

  .card {
    max-width: 270px;
    justify-self: center;
  }

  @media screen and (min-width: 767px) {
    grid-template-columns: repeat(2, 270px);
    gap: 20px;

    .card {
      width: 270px;
    }
  }

  @media screen and (min-width: 1200px) {
    grid-template-columns: repeat(4, 270px);
    gap: 20px;
  }
}

.card {
  overflow: hidden;
  padding: 6px 15px;

  @media screen and (min-width: 560px) {
    padding: 6px 75px;
  }
}
.w-layout-grid {
  grid-row-gap: 16px;
  grid-column-gap: 16px;
  grid-template-rows: auto auto;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  grid-auto-columns: 1fr;
  display: grid;
}

.offers-title {
  text-align: center;
  font-size: 24px;
  margin-bottom: 20px;
  color: var(--main-text-color);
  font-weight: 700;
  font-family: Grenze, sans-serif;
}

.offers-wrapper {
  border-radius: 12px;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid var(--blue-2);
  background: var(--blue-3);
  margin-bottom: 20px;
}

.sale-wrapper,
.shop-wrapper {
  grid-area: span 1 / span 1 / span 1 / span 1;
}

.offer {
  display: flex;
  align-items: baseline;
  transition: ease all 200ms;
  cursor: pointer;
}

.card.pd-48px---32px {
  padding: 0 10px;
}

.card.pd-48px---32px.shop-item {
  color: rgba(255, 255, 255, 0.5);
  background-color: #021b26;
  background-image: radial-gradient(circle closest-corner at 50% 0, #3b7285, rgba(0, 0, 0, 0));
  padding-top: 20px;
  padding-bottom: 20px;
  text-decoration: none;
  transition:
    all 0.3s,
    color 0.3s;

  &:hover {
    background-image: radial-gradient(circle closest-corner at 50% 0, #3faa58, rgba(0, 0, 0, 0));
  }
}

@media screen and (max-width: 479px) {
  .text-block {
    text-align: center;
  }
}

@media screen and (max-width: 991px) {
  .card.pd-48px---32px.shop-item {
    background-image: radial-gradient(circle farthest-side at 50% 0, #3b7285, rgba(0, 0, 0, 0) 50%);
    flex-direction: column;
    align-items: stretch;
    padding: 16px 20px;
    display: flex;
  }
}

@media screen and (max-width: 767px) {
  .card.pd-48px---32px.shop-item {
    padding-left: 16px;
    padding-right: 16px;
  }
}

@media screen and (max-width: 479px) {
  .card.pd-48px---32px.shop-item {
    padding: 12px 8px;
    font-size: 12px;
  }
}

.grid-wrapper {
  grid-column-gap: 28px;
  grid-row-gap: 20px;
  grid-template-rows: auto;
  grid-template-columns: 1fr;
  grid-auto-columns: 1fr;
  display: grid;
}

@media screen and (max-width: 991px) {
  .grid-wrapper {
    grid-column-gap: 16px;
    grid-row-gap: 16px;
    grid-template-columns: 1fr;
  }
}

@media screen and (max-width: 767px) {
  .grid-wrapper {
    flex-direction: column;
    display: flex;
  }
}
</style>
